/**
 * Mischief resolution: turns "the cat did a thing" into heat, noise, a trace,
 * and a chaos score, using the catalog in data/mischief.json.
 *
 * This is the one place that decides whether an action was witnessed, so the
 * witnessed/unseen split stays consistent everywhere.
 */

import catalog from '@data/mischief.json';
import type { TraceKind } from '@/core/events';
import type { Vec3 } from '@/core/math';
import { warnOnce } from '@/core/assert';

export interface MischiefAction {
  id: string;
  label: string;
  /** 0-100 loudness at the source. */
  noise: number;
  heatWitnessed: number;
  heatUnseen: number;
  trace: TraceKind | null;
  heatOnTraceFound: number;
  chaos: number;
  audioTag: string;
}

const actions = new Map<string, MischiefAction>(
  (catalog.actions as unknown as MischiefAction[]).map((a) => [a.id, a]),
);

export function getMischiefAction(id: string): MischiefAction | undefined {
  return actions.get(id);
}

export function allMischiefActions(): MischiefAction[] {
  return [...actions.values()];
}

export interface MischiefResolution {
  action: MischiefAction;
  /** Heat to add immediately. */
  heat: number;
  /** Trace to create, if any. Heat for finding it is deferred until discovery. */
  trace: { kind: TraceKind; heatOnFound: number } | null;
  noise: { loudness: number; tag: string; position: Vec3 };
  chaos: number;
  witnessed: boolean;
}

/**
 * Resolves a mischief event.
 *
 * `witnessed` means granny had line of sight at the moment it happened. Note
 * that an unwitnessed action can still be *heard* - the noise event fires
 * regardless, and her hearing sensor decides what to do about it. Being unseen
 * is not the same as being unnoticed.
 */
export function resolveMischief(
  actionId: string,
  position: Vec3,
  witnessed: boolean,
): MischiefResolution | null {
  const action = actions.get(actionId);
  if (!action) {
    warnOnce(
      `mischief:${actionId}`,
      `Unknown mischief action "${actionId}" - check data/mischief.json`,
    );
    return null;
  }

  return {
    action,
    heat: witnessed ? action.heatWitnessed : action.heatUnseen,
    trace: action.trace ? { kind: action.trace, heatOnFound: action.heatOnTraceFound } : null,
    noise: { loudness: action.noise, tag: action.audioTag, position },
    chaos: action.chaos,
    witnessed,
  };
}

/** Growth profile for traces that worsen when ignored. Currently just the faucet. */
export function growthForTrace(kind: TraceKind): { perSecond: number; max: number } | undefined {
  if (kind === 'flood') return { perSecond: 0.4, max: 20 };
  return undefined;
}
